import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Plus, Building2, Mail, Phone, Globe, KeyRound, Pencil, ShieldCheck, Search, UserCircle2, Shield, Trash2, UsersRound, Lock, Unlock, RotateCcw, Ship, FileText, Download, Loader2, X as XIcon } from "lucide-react";
import { triggerNativeDownload } from "@/lib/api";

export default function Buyers() {
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const [tab, setTab] = useState("buyers"); // 'buyers' | 'team'
  const [query, setQuery] = useState("");
  const [buyers, setBuyers] = useState([]);
  const [team, setTeam] = useState([]);
  const [loadingBuyers, setLoadingBuyers] = useState(true);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [credBuyer, setCredBuyer] = useState(null);
  const [editBuyer, setEditBuyer] = useState(null);
  const [docsBuyer, setDocsBuyer] = useState(null);
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);

  const reloadBuyers = async () => {
    setLoadingBuyers(true);
    try {
      const r = await api.get("/buyers");
      setBuyers(r.data);
    } finally { setLoadingBuyers(false); }
  };
  const reloadTeam = async () => {
    setLoadingTeam(true);
    try {
      const r = await api.get("/team");
      setTeam(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setLoadingTeam(false); }
  };
  useEffect(() => { reloadBuyers(); }, []);
  useEffect(() => { if (tab === "team" && team.length === 0) reloadTeam(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset search when switching tabs
  useEffect(() => { setQuery(""); }, [tab]);

  const filteredBuyers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return buyers;
    return buyers.filter((b) => [b.company_name, b.contact_person, b.email, b.country, b.phone, b.login_email]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [buyers, query]);

  const filteredTeam = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return team;
    return team.filter((m) => [m.name, m.email].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [team, query]);

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email} from the team? They will lose access immediately.`)) return;
    try {
      await api.delete(`/team/${m.id}`);
      toast.success("Team member removed");
      reloadTeam();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const toggleOwner = async (m) => {
    const next = !m.is_owner;
    const msg = next
      ? `Promote ${m.name || m.email} to OWNER? They'll be able to manage the team and approve gated stages.`
      : `Revoke owner privileges from ${m.name || m.email}? They'll remain an admin but lose team-management and approval rights.`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/team/${m.id}/owner`, { is_owner: next });
      toast.success(next ? "Promoted to owner" : "Owner privileges revoked");
      reloadTeam();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const toggleMemberLock = async (m) => {
    const next = !m.disabled;
    if (next && !window.confirm(`Lock ${m.name || m.email}? They will not be able to sign in until unlocked.`)) return;
    try {
      await api.post(`/team/${m.id}/lock`, { locked: next });
      toast.success(next ? "Team member locked" : "Team member unlocked");
      reloadTeam();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const deleteBuyer = async (b) => {
    if (!window.confirm(`Delete buyer "${b.company_name}" permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/buyers/${b.id}`);
      toast.success("Buyer deleted");
      reloadBuyers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const toggleBuyerLock = async (b) => {
    const next = !b.login_disabled;
    if (next && !window.confirm(`Lock ${b.company_name}'s portal access? They will not be able to sign in until unlocked.`)) return;
    try {
      await api.post(`/buyers/${b.id}/credentials/lock`, { locked: next });
      toast.success(next ? "Buyer access locked" : "Buyer access unlocked");
      reloadBuyers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const [resetTarget, setResetTarget] = useState(null); // {kind:'team'|'buyer', id, label}

  return (
    <div className="p-5 space-y-4">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#1F1F1F]">Directory</h1>
        <p className="text-stone-500 text-sm mt-0.5">Manage buyers and your TradeRepubliq team in one place.</p>
      </div>

      {/* Segment toggle */}
      <div className="bg-stone-100 p-1 rounded-xl inline-flex w-full" data-testid="dir-tabs">
        <button
          onClick={() => setTab("buyers")}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-semibold py-2 rounded-lg transition-all ${tab === "buyers" ? "bg-white text-[#6B0F1A] shadow-sm" : "text-stone-500 hover:text-[#1F1F1F]"}`}
          data-testid="tab-buyers"
        >
          <Building2 className="w-4 h-4" /> Buyers
          <span className="text-[10px] font-bold bg-stone-200 text-stone-600 rounded-full px-1.5 py-0.5">{buyers.length}</span>
        </button>
        <button
          onClick={() => setTab("team")}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-semibold py-2 rounded-lg transition-all ${tab === "team" ? "bg-white text-[#6B0F1A] shadow-sm" : "text-stone-500 hover:text-[#1F1F1F]"}`}
          data-testid="tab-team"
        >
          <UsersRound className="w-4 h-4" /> Team
          {team.length > 0 && <span className="text-[10px] font-bold bg-stone-200 text-stone-600 rounded-full px-1.5 py-0.5">{team.length}</span>}
        </button>
      </div>

      {/* Search + primary action */}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={tab === "buyers" ? "Search by company, contact, email…" : "Search by name or email…"}
            className="w-full bg-white border border-stone-200 rounded-xl pl-9 pr-3 py-2.5 text-sm outline-none focus:border-[#A8323F] focus:ring-2 focus:ring-[#A8323F]/15"
            data-testid="dir-search"
          />
        </div>
        {tab === "buyers" ? (
          <Link
            to="/app/buyers/new"
            className="shrink-0 inline-flex items-center gap-1.5 bg-[#6B0F1A] text-white text-sm font-semibold rounded-xl px-3.5 py-2.5 hover:bg-[#56121C] active:scale-[0.98]"
            data-testid="new-buyer-btn"
          >
            <Plus className="w-4 h-4" /> New
          </Link>
        ) : (
          isOwner ? (
            <button
              onClick={() => setCreatingTeam(true)}
              className="shrink-0 inline-flex items-center gap-1.5 bg-[#6B0F1A] text-white text-sm font-semibold rounded-xl px-3.5 py-2.5 hover:bg-[#56121C] active:scale-[0.98]"
              data-testid="new-team-btn"
            >
              <Plus className="w-4 h-4" /> Add
            </button>
          ) : null
        )}
      </div>

      {tab === "team" && !isOwner && (
        <div className="bg-stone-50 border border-stone-200 text-stone-600 rounded-xl px-4 py-2.5 text-xs flex items-center gap-2" data-testid="team-readonly-banner">
          <Shield className="w-3.5 h-3.5 text-[#6B0F1A]" /> Only the account owner can add or remove team members. You can update your own profile.
        </div>
      )}

      {/* Buyers tab content */}
      {tab === "buyers" && (
        <div className="space-y-2" data-testid="buyers-list">
          {loadingBuyers && <div className="text-sm text-stone-400">Loading…</div>}
          {!loadingBuyers && buyers.length === 0 && (
            <EmptyState icon={<Building2 className="w-8 h-8 text-stone-300" />} title="No buyers yet" subtitle="Register a buyer to begin creating shipments." />
          )}
          {!loadingBuyers && buyers.length > 0 && filteredBuyers.length === 0 && (
            <NoResults query={query} />
          )}
          {filteredBuyers.map((b) => (
            <div key={b.id} className="bg-white border border-stone-200 rounded-2xl p-4" data-testid={`buyer-card-${b.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#F4E8E9] text-[#6B0F1A] flex items-center justify-center">
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#1F1F1F] flex items-center gap-2 flex-wrap">
                  <span className="truncate">{b.company_name}</span>
                  {b.reference_initials && (
                    <span className="inline-flex items-center text-[10px] uppercase tracking-[0.15em] bg-[#F4E8E9] text-[#6B0F1A] border border-[#6B0F1A]/15 px-1.5 py-0.5 rounded-full font-mono font-bold" data-testid={`buyer-initials-${b.id}`} title="Shipment reference initials">
                      {b.reference_initials}
                    </span>
                  )}
                  {b.has_login && !b.login_disabled && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full" data-testid={`buyer-has-login-${b.id}`}>
                      <ShieldCheck className="w-3 h-3" /> Login
                    </span>
                  )}
                  {b.has_login && b.login_disabled && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-full" data-testid={`buyer-locked-${b.id}`}>
                      <Lock className="w-3 h-3" /> Locked
                    </span>
                  )}
                </div>
                  <div className="text-xs text-stone-500">{b.contact_person}</div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-stone-500">
                    <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" />{b.country}</span>
                    <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{b.email}</span>
                    <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{b.phone}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`buyer-shipments-${b.id}`}>
                    <Link
                      to={`/app/shipments?buyer=${b.id}`}
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-[#F4E8E9] hover:bg-[#EBD3D6] text-[#6B0F1A] border border-[#6B0F1A]/15 px-2 py-1 rounded-full transition-colors"
                      data-testid={`buyer-shipments-link-${b.id}`}
                    >
                      <Ship className="w-3 h-3" />
                      {b.shipment_count || 0} shipment{(b.shipment_count || 0) === 1 ? "" : "s"}
                    </Link>
                    {!!b.active_shipment_count && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full" data-testid={`buyer-active-shipments-${b.id}`}>
                        {b.active_shipment_count} active
                      </span>
                    )}
                    {b.is_established && (
                      <button
                        onClick={() => setDocsBuyer(b)}
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-1 rounded-full transition-colors"
                        data-testid={`buyer-onboarding-docs-${b.id}`}
                      >
                        <FileText className="w-3 h-3" />
                        KYC & SLA
                      </button>
                    )}
                  </div>
                  {b.has_login && b.login_email && (
                    <div className="text-[11px] text-stone-400 mt-1.5 font-mono">Login: {b.login_email}</div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-4 gap-1.5 mt-3 pt-3 border-t border-stone-100" data-testid={`buyer-actions-${b.id}`}>
                <ActionBtn label="Edit" icon={<Pencil className="w-3.5 h-3.5" />} color="blue" onClick={() => setEditBuyer(b)} testId={`buyer-edit-${b.id}`} />
                <ActionBtn
                  label={!b.has_login ? "Grant" : (b.login_disabled ? "Unlock" : "Lock")}
                  icon={b.login_disabled ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                  color="amber"
                  onClick={() => b.has_login ? toggleBuyerLock(b) : setCredBuyer(b)}
                  testId={`buyer-lock-${b.id}`}
                />
                <ActionBtn
                  label="Reset"
                  icon={<RotateCcw className="w-3.5 h-3.5" />}
                  color="slate"
                  disabled={!b.has_login}
                  onClick={() => b.has_login ? setResetTarget({ kind: "buyer", id: b.id, label: b.company_name }) : setCredBuyer(b)}
                  testId={`buyer-reset-${b.id}`}
                />
                <ActionBtn label="Delete" icon={<Trash2 className="w-3.5 h-3.5" />} color="red" onClick={() => deleteBuyer(b)} testId={`buyer-delete-${b.id}`} disabled={!isOwner} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Team tab content */}
      {tab === "team" && (
        <div className="space-y-2" data-testid="team-list">
          {loadingTeam && <div className="text-sm text-stone-400">Loading…</div>}
          {!loadingTeam && team.length === 0 && (
            <EmptyState icon={<UserCircle2 className="w-8 h-8 text-stone-300" />} title="No team members yet" subtitle="Add a TradeRepubliq employee to give them admin access." />
          )}
          {!loadingTeam && team.length > 0 && filteredTeam.length === 0 && <NoResults query={query} />}
          {filteredTeam.map((m) => (
            <div key={m.id} className="bg-white border border-stone-200 rounded-2xl p-4" data-testid={`team-card-${m.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#F4E8E9] text-[#6B0F1A] flex items-center justify-center">
                  <UserCircle2 className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#1F1F1F] flex items-center gap-2 flex-wrap">
                    <span className="truncate">{m.name || m.email}</span>
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#F4E8E9] text-[#6B0F1A] border border-[#6B0F1A]/15 px-1.5 py-0.5 rounded-full">
                      <Shield className="w-3 h-3" /> Admin
                    </span>
                    {m.is_owner && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded-full">
                        Owner
                      </span>
                    )}
                    {m.is_self && (
                      <span className="text-[10px] uppercase tracking-wider bg-stone-100 text-stone-600 px-1.5 py-0.5 rounded-full">You</span>
                    )}
                    {m.disabled && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-full" data-testid={`team-locked-${m.id}`}>
                        <Lock className="w-3 h-3" /> Locked
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-stone-500 inline-flex items-center gap-1 mt-1"><Mail className="w-3 h-3" /> {m.email}</div>
                  <div className="text-[11px] text-stone-400 mt-1">Added {new Date(m.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-1.5 mt-3 pt-3 border-t border-stone-100" data-testid={`team-actions-${m.id}`}>
                <ActionBtn
                  label={m.is_self ? "Profile" : "Edit"}
                  icon={<Pencil className="w-3.5 h-3.5" />}
                  color="blue"
                  onClick={() => setEditingTeam(m)}
                  disabled={!isOwner && !m.is_self}
                  testId={`team-edit-${m.id}`}
                />
                <ActionBtn
                  label={m.disabled ? "Unlock" : "Lock"}
                  icon={m.disabled ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                  color="amber"
                  onClick={() => toggleMemberLock(m)}
                  disabled={!isOwner || m.is_self || m.is_owner}
                  testId={`team-lock-${m.id}`}
                />
                <ActionBtn
                  label="Reset"
                  icon={<RotateCcw className="w-3.5 h-3.5" />}
                  color="slate"
                  onClick={() => setResetTarget({ kind: "team", id: m.id, label: m.name || m.email })}
                  disabled={!isOwner && !m.is_self}
                  testId={`team-reset-${m.id}`}
                />
                <ActionBtn
                  label="Delete"
                  icon={<Trash2 className="w-3.5 h-3.5" />}
                  color="red"
                  onClick={() => removeMember(m)}
                  disabled={!isOwner || m.is_self || m.is_owner}
                  testId={`team-remove-${m.id}`}
                />
              </div>
              {isOwner && !m.is_self && (
                <div className="flex justify-end mt-2">
                  <button
                    onClick={() => toggleOwner(m)}
                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-lg px-2.5 py-1 border transition-colors ${
                      m.is_owner
                        ? "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
                        : "bg-[#F4E8E9] text-[#6B0F1A] border-[#6B0F1A]/15 hover:bg-[#EAD8DA]"
                    }`}
                    data-testid={`team-toggle-owner-${m.id}`}
                  >
                    <Shield className="w-3 h-3" /> {m.is_owner ? "Revoke owner" : "Make owner"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {credBuyer && (
        <CredentialsModal buyer={credBuyer} onClose={() => setCredBuyer(null)} onSaved={reloadBuyers} />
      )}
      {editBuyer && (
        <EditBuyerModal buyer={editBuyer} onClose={() => setEditBuyer(null)} onSaved={reloadBuyers} />
      )}
      {docsBuyer && (
        <OnboardingDocsModal buyer={docsBuyer} onClose={() => setDocsBuyer(null)} />
      )}
      {creatingTeam && <TeamCreateModal onClose={() => setCreatingTeam(false)} onSaved={reloadTeam} />}
      {editingTeam && <TeamEditModal member={editingTeam} onClose={() => setEditingTeam(null)} onSaved={reloadTeam} />}
      {resetTarget && (
        <ResetPasswordModal
          target={resetTarget}
          onClose={() => setResetTarget(null)}
          onSaved={() => { resetTarget.kind === "team" ? reloadTeam() : reloadBuyers(); }}
        />
      )}
    </div>
  );
}

function ActionBtn({ label, icon, color, onClick, disabled, testId }) {
  const palette = {
    blue:  "bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100",
    amber: "bg-amber-50 text-amber-800 border-amber-100 hover:bg-amber-100",
    slate: "bg-slate-50 text-slate-700 border-slate-100 hover:bg-slate-100",
    red:   "bg-red-50 text-red-700 border-red-100 hover:bg-red-100",
  }[color] || "bg-stone-50 text-stone-700 border-stone-100";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg border text-[10px] font-bold uppercase tracking-wider transition-colors ${palette} ${disabled ? "opacity-40 cursor-not-allowed" : "active:scale-[0.98]"}`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function EmptyState({ icon, title, subtitle }) {
  return (
    <div className="bg-white border border-dashed border-stone-300 rounded-2xl p-8 text-center">
      <div className="mx-auto">{icon}</div>
      <div className="font-semibold text-[#1F1F1F] mt-3">{title}</div>
      <div className="text-sm text-stone-500 mt-1">{subtitle}</div>
    </div>
  );
}

function NoResults({ query }) {
  return (
    <div className="bg-white border border-dashed border-stone-300 rounded-2xl p-6 text-center text-sm text-stone-500" data-testid="dir-no-results">
      No results matching <span className="font-semibold text-[#1F1F1F]">"{query}"</span>.
    </div>
  );
}

function CredentialsModal({ buyer, onClose, onSaved }) {
  const [email, setEmail] = useState(buyer.login_email || buyer.email || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState(buyer.contact_person || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/buyers/${buyer.id}/credentials`, { email: email.trim().toLowerCase(), password, name });
      toast.success(buyer.has_login ? "Buyer access updated" : "Buyer access created");
      onSaved && onSaved();
      onClose();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm("Revoke this buyer's portal login?")) return;
    setBusy(true);
    try {
      await api.delete(`/buyers/${buyer.id}/credentials`);
      toast.success("Buyer access revoked");
      onSaved && onSaved();
      onClose();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="credentials-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-4 my-auto max-h-[85vh] overflow-y-auto">
        <div>
          <h3 className="font-display text-lg font-extrabold text-[#1F1F1F]">{buyer.has_login ? "Update buyer access" : "Grant buyer access"}</h3>
          <p className="text-xs text-stone-500 mt-1">{buyer.company_name} can sign in with these credentials and view only their own shipments.</p>
        </div>
        <Field label="Login email">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input" data-testid="cred-email" />
        </Field>
        <Field label="Password (min 6 chars)">
          <input type="text" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="input" placeholder={buyer.has_login ? "Set new password" : "Set initial password"} data-testid="cred-password" />
        </Field>
        <Field label="Display name">
          <input value={name} onChange={(e) => setName(e.target.value)} className="input" data-testid="cred-name" />
        </Field>
        <div className="flex items-center justify-between gap-2 pt-2">
          {buyer.has_login ? (
            <button type="button" onClick={revoke} disabled={busy} className="text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-50" data-testid="cred-revoke">Revoke access</button>
          ) : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="text-sm text-stone-500 px-3 py-2">Cancel</button>
            <button type="submit" disabled={busy} className="bg-[#6B0F1A] hover:bg-[#56121C] text-white text-sm font-semibold rounded-xl px-4 py-2 disabled:opacity-60" data-testid="cred-save">{busy ? "Saving…" : (buyer.has_login ? "Update" : "Grant access")}</button>
          </div>
        </div>
        <style>{`.input { width:100%; background:#FBFAF9; border:1px solid #E5E0DC; border-radius:.5rem; padding:.65rem .85rem; color:#1F1F1F; outline:none; } .input:focus { background:white; border-color:#A8323F; box-shadow:0 0 0 3px rgba(168,50,63,0.15);} `}</style>
      </form>
    </div>,
    document.body
  );
}

function EditBuyerModal({ buyer, onClose, onSaved }) {
  const [form, setForm] = useState({
    company_name: buyer.company_name || "",
    contact_person: buyer.contact_person || "",
    email: buyer.email || "",
    phone: buyer.phone || "",
    country: buyer.country || "",
    address: buyer.address || "",
    reference_initials: buyer.reference_initials || "",
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/buyers/${buyer.id}`, form);
      toast.success("Buyer updated");
      onSaved && onSaved();
      onClose();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="edit-buyer-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-3 my-auto max-h-[85vh] overflow-y-auto">
        <h3 className="font-display text-lg font-extrabold text-[#1F1F1F]">Edit buyer</h3>
        <Field label="Company Name">
          <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="input" data-testid="edit-buyer-company" />
        </Field>
        <Field label="Shipment Reference Initials">
          <input
            value={form.reference_initials}
            onChange={(e) => setForm({ ...form, reference_initials: e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 5) })}
            placeholder="e.g. ADN"
            maxLength={5}
            className="input uppercase tracking-widest font-semibold"
            data-testid="edit-buyer-reference-initials"
          />
          <p className="text-[11px] text-stone-500 mt-1">Used in shipment & SLA reference numbers. Leave blank to auto-derive from the company name.</p>
        </Field>
        <Field label="Contact Person">
          <input required value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="input" data-testid="edit-buyer-contact" />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Email">
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" data-testid="edit-buyer-email" />
          </Field>
          <Field label="Phone">
            <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" data-testid="edit-buyer-phone" />
          </Field>
        </div>
        <Field label="Country">
          <input required value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="input" data-testid="edit-buyer-country" />
        </Field>
        <Field label="Address">
          <textarea rows={2} required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input" data-testid="edit-buyer-address" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="text-sm text-stone-500 px-3 py-2">Cancel</button>
          <button type="submit" disabled={busy} className="bg-[#6B0F1A] hover:bg-[#56121C] text-white text-sm font-semibold rounded-xl px-4 py-2 disabled:opacity-60" data-testid="edit-buyer-save">{busy ? "Saving…" : "Save changes"}</button>
        </div>
        <style>{`.input { width:100%; background:#FBFAF9; border:1px solid #E5E0DC; border-radius:.5rem; padding:.55rem .75rem; color:#1F1F1F; outline:none; font-size:.9rem; } .input:focus { background:white; border-color:#A8323F; box-shadow:0 0 0 3px rgba(168,50,63,0.15);} `}</style>
      </form>
    </div>,
    document.body
  );
}

export function NewBuyer() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    country: "",
    address: "",
    reference_initials: "",
  });
  // Auto-suggest initials from company name when operator hasn't manually typed them.
  const [initialsTouched, setInitialsTouched] = useState(false);
  const suggestedInitials = useMemo(() => {
    const s = (form.company_name || "").toUpperCase().replace(/[^A-Z]/g, "");
    if (!s) return "";
    const vowels = new Set(["A", "E", "I", "O", "U"]);
    const out = [s[0]];
    for (const ch of s.slice(1)) {
      if (out.length >= 3) break;
      if (!vowels.has(ch)) out.push(ch);
    }
    for (const ch of s.slice(1)) {
      if (out.length >= 3) break;
      if (!out.includes(ch)) out.push(ch);
    }
    return out.join("");
  }, [form.company_name]);
  const effectiveInitials = (initialsTouched ? form.reference_initials : suggestedInitials).toUpperCase();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, reference_initials: effectiveInitials };
      await api.post("/buyers", payload);
      toast.success("Buyer registered");
      nav("/app/buyers");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-5 space-y-5">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-[#6B0F1A]">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#1F1F1F]">Register Buyer</h1>
        <p className="text-stone-500 text-sm mt-1">Save buyer profile for future shipments. You can grant portal access after creation.</p>
      </div>
      <form onSubmit={submit} className="bg-white border border-stone-200 rounded-2xl p-5 space-y-4" data-testid="new-buyer-form">
        <Field label="Company Name">
          <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="input" data-testid="buyer-company" />
        </Field>
        <Field label="Shipment Reference Initials">
          <input
            value={initialsTouched ? form.reference_initials : suggestedInitials}
            onChange={(e) => {
              setInitialsTouched(true);
              setForm({ ...form, reference_initials: e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 5) });
            }}
            placeholder={suggestedInitials || "e.g. ADN"}
            maxLength={5}
            className="input uppercase tracking-widest font-semibold"
            data-testid="buyer-reference-initials"
          />
          <p className="text-[11px] text-stone-500 mt-1">
            These letters will be used in shipment & SLA reference numbers (e.g. <span className="font-mono text-[#6B0F1A]">{effectiveInitials || "ADN"}-AVC-001</span>, <span className="font-mono text-[#6B0F1A]">SLA-TRQ-{effectiveInitials || "ADN"}-001</span>). Auto-suggested from the company name — edit if needed.
          </p>
        </Field>
        <Field label="Contact Person">
          <input required value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="input" data-testid="buyer-contact" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Email">
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" data-testid="buyer-email" />
          </Field>
          <Field label="Phone">
            <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" data-testid="buyer-phone" />
          </Field>
        </div>
        <Field label="Country">
          <input required value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="input" data-testid="buyer-country" />
        </Field>
        <Field label="Address">
          <textarea rows={3} required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input" data-testid="buyer-address" />
        </Field>
        <button type="submit" disabled={busy} className="w-full bg-[#6B0F1A] hover:bg-[#56121C] text-white font-semibold rounded-xl py-3 disabled:opacity-60 active:scale-[0.98]" data-testid="submit-buyer">
          {busy ? "Saving…" : "Save buyer"}
        </button>
      </form>
      <style>{`.input { width:100%; background:#FBFAF9; border:1px solid #E5E0DC; border-radius:.5rem; padding:.75rem 1rem; color:#1F1F1F; outline:none; } .input:focus { background:white; border-color:#A8323F; box-shadow:0 0 0 3px rgba(168,50,63,0.15);} `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-widest text-stone-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}


function TeamCreateModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = ""; }; }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/team", { ...form, email: form.email.trim().toLowerCase() });
      toast.success("Team member added");
      onSaved && onSaved();
      onClose();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="team-create-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-4 my-auto max-h-[85vh] overflow-y-auto">
        <div>
          <h3 className="font-display text-lg font-extrabold text-[#1F1F1F]">Add team member</h3>
          <p className="text-xs text-stone-500 mt-1">They will be able to sign in and access the full operations suite as an admin.</p>
        </div>
        <Field label="Full name">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" data-testid="team-create-name" />
        </Field>
        <Field label="Login email">
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" data-testid="team-create-email" />
        </Field>
        <Field label="Password (min 6 chars)">
          <input type="text" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input" data-testid="team-create-password" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="text-sm text-stone-500 px-3 py-2">Cancel</button>
          <button type="submit" disabled={busy} className="bg-[#6B0F1A] hover:bg-[#56121C] text-white text-sm font-semibold rounded-xl px-4 py-2 disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="team-create-save">
            <KeyRound className="w-3.5 h-3.5" /> {busy ? "Adding…" : "Add member"}
          </button>
        </div>
        <style>{`.input { width:100%; background:#FBFAF9; border:1px solid #E5E0DC; border-radius:.5rem; padding:.65rem .85rem; color:#1F1F1F; outline:none; } .input:focus { background:white; border-color:#A8323F; box-shadow:0 0 0 3px rgba(168,50,63,0.15);} `}</style>
      </form>
    </div>,
    document.body
  );
}

function TeamEditModal({ member, onClose, onSaved }) {
  const [name, setName] = useState(member.name || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = ""; }; }, []);
  const submit = async (e) => {
    e.preventDefault();
    const payload = {};
    if (name && name !== member.name) payload.name = name;
    if (password) {
      if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
      payload.password = password;
    }
    if (Object.keys(payload).length === 0) { toast.info("No changes to save"); return; }
    setBusy(true);
    try {
      await api.put(`/team/${member.id}`, payload);
      toast.success("Team member updated");
      onSaved && onSaved();
      onClose();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="team-edit-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-4 my-auto max-h-[85vh] overflow-y-auto">
        <div>
          <h3 className="font-display text-lg font-extrabold text-[#1F1F1F]">{member.is_self ? "Update my profile" : "Edit team member"}</h3>
          <p className="text-xs text-stone-500 mt-1">{member.email}</p>
        </div>
        <Field label="Full name">
          <input value={name} onChange={(e) => setName(e.target.value)} className="input" data-testid="team-edit-name" />
        </Field>
        <Field label={member.is_self ? "New password (leave blank to keep current)" : "Reset password (leave blank to keep)"}>
          <input type="text" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="input" placeholder="Leave blank to keep" data-testid="team-edit-password" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="text-sm text-stone-500 px-3 py-2">Cancel</button>
          <button type="submit" disabled={busy} className="bg-[#6B0F1A] hover:bg-[#56121C] text-white text-sm font-semibold rounded-xl px-4 py-2 disabled:opacity-60" data-testid="team-edit-save">{busy ? "Saving…" : "Save changes"}</button>
        </div>
        <style>{`.input { width:100%; background:#FBFAF9; border:1px solid #E5E0DC; border-radius:.5rem; padding:.65rem .85rem; color:#1F1F1F; outline:none; } .input:focus { background:white; border-color:#A8323F; box-shadow:0 0 0 3px rgba(168,50,63,0.15);} `}</style>
      </form>
    </div>,
    document.body
  );
}

function ResetPasswordModal({ target, onClose, onSaved }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = ""; }; }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      const url = target.kind === "team"
        ? `/team/${target.id}/reset-password`
        : `/buyers/${target.id}/credentials/reset-password`;
      await api.post(url, { password });
      toast.success("Password reset");
      onSaved && onSaved();
      onClose();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="reset-password-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-4 my-auto max-h-[85vh] overflow-y-auto">
        <div>
          <h3 className="font-display text-lg font-extrabold text-[#1F1F1F]">Reset password</h3>
          <p className="text-xs text-stone-500 mt-1">Set a new password for <span className="font-semibold text-[#1F1F1F]">{target.label}</span>. Share it with them through a secure channel.</p>
        </div>
        <Field label="New password (min 6 chars)">
          <input type="text" autoFocus required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="input" data-testid="reset-password-input" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="text-sm text-stone-500 px-3 py-2">Cancel</button>
          <button type="submit" disabled={busy} className="bg-[#6B0F1A] hover:bg-[#56121C] text-white text-sm font-semibold rounded-xl px-4 py-2 disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="reset-password-save">
            <RotateCcw className="w-3.5 h-3.5" /> {busy ? "Resetting…" : "Reset password"}
          </button>
        </div>
        <style>{`.input { width:100%; background:#FBFAF9; border:1px solid #E5E0DC; border-radius:.5rem; padding:.65rem .85rem; color:#1F1F1F; outline:none; } .input:focus { background:white; border-color:#A8323F; box-shadow:0 0 0 3px rgba(168,50,63,0.15);} `}</style>
      </form>
    </div>,
    document.body
  );
}

function OnboardingDocsModal({ buyer, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = ""; }; }, []);
  useEffect(() => {
    let active = true;
    api.get(`/buyers/${buyer.id}/onboarding-docs`)
      .then((r) => { if (active) setData(r.data); })
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [buyer.id]);

  const downloadDoc = async (doc) => {
    setBusyId(doc.id);
    try {
      const r = await api.post(`/shipments/${doc.shipment_id}/documents/${doc.id}/download-token`);
      triggerNativeDownload(r.data.download_url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not fetch document");
    } finally { setBusyId(null); }
  };
  const viewDoc = async (doc) => {
    // For images we open inline via base64; for PDFs/everything else we trigger
    // the same native download that opens a viewer tab on iOS / Android.
    setBusyId(doc.id);
    try {
      if ((doc.mime_type || "").startsWith("image/")) {
        const r = await api.get(`/shipments/${doc.shipment_id}/documents/${doc.id}`);
        const b64 = r.data?.content_base64;
        if (!b64) { toast.error("Could not fetch document"); return; }
        const w = window.open();
        if (!w) { toast.error("Popup blocked — allow popups to preview images"); return; }
        w.document.write(`<img src="${b64}" style="max-width:100%" alt="${doc.filename}" />`);
        return;
      }
      const r = await api.post(`/shipments/${doc.shipment_id}/documents/${doc.id}/download-token`);
      triggerNativeDownload(r.data.download_url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not fetch document");
    } finally { setBusyId(null); }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="onboarding-docs-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg my-auto max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-start justify-between p-5 border-b border-stone-100">
          <div>
            <h3 className="font-display text-lg font-extrabold text-[#1F1F1F]">Onboarding documents</h3>
            <p className="text-xs text-stone-500 mt-1 truncate">{buyer.company_name}</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-[#6B0F1A]" data-testid="onboarding-docs-close">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading ? (
            <div className="text-sm text-stone-400 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading documents…
            </div>
          ) : (
            <>
              <DocSection
                title="Step 1 · Buyer Registration (KYC)"
                docs={data?.buyer_docs || []}
                emptyHint="No KYC documents have been uploaded for this buyer yet."
                busyId={busyId}
                onView={viewDoc}
                onDownload={downloadDoc}
                testIdPrefix="buyer-doc"
              />
              <DocSection
                title="Step 2 · Service Level Agreement"
                docs={data?.sla_docs || []}
                emptyHint="No SLA documents on file."
                busyId={busyId}
                onView={viewDoc}
                onDownload={downloadDoc}
                testIdPrefix="sla-doc"
              />
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function DocSection({ title, docs, emptyHint, busyId, onView, onDownload, testIdPrefix }) {
  return (
    <section data-testid={`${testIdPrefix}-section`}>
      <div className="text-xs font-bold uppercase tracking-wider text-[#6B0F1A] mb-2">{title}</div>
      {docs.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-stone-200 bg-stone-50/40 px-4 py-5 text-center text-xs text-stone-400" data-testid={`${testIdPrefix}-empty`}>
          {emptyHint}
        </div>
      ) : (
        <div className="space-y-1.5">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2 bg-white border border-stone-200 rounded-lg px-3 py-2" data-testid={`${testIdPrefix}-${d.id}`}>
              <div className="w-7 h-7 rounded-md bg-[#F4E8E9] text-[#6B0F1A] flex items-center justify-center shrink-0">
                <FileText className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#1F1F1F] truncate">{d.filename}</div>
                <div className="text-[11px] text-stone-500 flex items-center gap-2 flex-wrap">
                  {d.shipment_reference && <span className="font-mono">{d.shipment_reference}</span>}
                  {d.uploaded_at && <span>· {new Date(d.uploaded_at).toLocaleDateString()}</span>}
                </div>
              </div>
              <button
                onClick={() => onView(d)}
                disabled={busyId === d.id}
                className="text-xs font-semibold text-stone-600 hover:text-[#6B0F1A] px-2 py-1.5 disabled:opacity-50"
                data-testid={`${testIdPrefix}-view-${d.id}`}
              >
                View
              </button>
              <button
                onClick={() => onDownload(d)}
                disabled={busyId === d.id}
                className="inline-flex items-center gap-1 text-xs font-semibold bg-[#6B0F1A] hover:bg-[#56121C] text-white rounded-lg px-2.5 py-1.5 disabled:opacity-50"
                data-testid={`${testIdPrefix}-download-${d.id}`}
              >
                {busyId === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                Download
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


